import { useQuery } from '@tanstack/react-query';
import { Icon } from '../components/Icon.tsx';
import { usersApi } from '../features/users/api.ts';
import { dv } from '../i18n/dv.ts';
import { displayName } from '../lib/utils.ts';

const OFFICE_URL = 'https://office.wapplify.app';

// Two-letter avatar seed — the display name, falling back to the email. Matches
// the sidebar's identity avatar.
function avatarInitials(u: { name: string; nameDv?: string | null; email: string }): string {
  return (displayName(u) || u.email).slice(0, 2).toUpperCase();
}

export function OrgUsers(): JSX.Element {
  const { data: users, isLoading } = useQuery({ queryKey: ['orgUsers'], queryFn: usersApi.list });
  const list = users ?? [];

  return (
    <div className="space-y-6">
      {/* User management has moved to Wapplify Office. This list is read-only. */}
      <section className="card-paper flex flex-wrap items-center justify-between gap-3 p-5 sm:p-6">
        <div>
          <p className="font-medium">{dv.users.managedInOffice}</p>
          <p className="text-sm text-muted-foreground">{dv.users.managedInOfficeHint}</p>
        </div>
        <a href={OFFICE_URL} target="_blank" rel="noopener noreferrer" className="btn-primary">
          {dv.users.openOffice}
          <Icon name="open_in_new" size={18} />
        </a>
      </section>

      <div className="space-y-3">
        <h2 className="section-heading text-lg font-bold">
          {dv.settings.usersTab}
          {list.length > 0 && <span className="pill pill-active shrink-0">{list.length}</span>}
        </h2>

        <section className="card-paper divide-y divide-border overflow-hidden p-0">
          {list.map((u) => (
            <div
              key={u.id}
              className={`flex items-center gap-3.5 p-4 transition-colors hover:bg-[hsl(var(--surface-high))] ${!u.isActive ? 'opacity-60' : ''}`}
            >
              {/* Avatar — initials on the printed-cream disc (same as the sidebar). */}
              <div className="grid size-10 shrink-0 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-base text-ink">
                {avatarInitials(u)}
              </div>

              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium leading-tight">
                  <span className="truncate">{displayName(u)}</span>
                  {u.isOrgAdmin && <span className="pill pill-active shrink-0">{dv.orgAdminBadge}</span>}
                  {!u.isActive && <span className="pill pill-neutral shrink-0">{dv.common.inactive}</span>}
                </p>
                <p className="mt-1 truncate text-sm text-muted-foreground">
                  {u.designationDv ?? dv.roles[u.role]}
                  {u.designationEn && <span className="ms-2 text-xs" dir="ltr">{u.designationEn}</span>}
                </p>
                <p className="mono-meta mt-0.5 truncate text-muted-foreground" dir="ltr">{u.email}</p>
              </div>
            </div>
          ))}
          {list.length === 0 && (
            <p className="p-8 text-center text-sm text-muted-foreground">
              {isLoading ? dv.common.loading : dv.users.empty}
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
